"use client";

import { Fragment, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { Search, SlidersHorizontal } from "lucide-react";
import { BlogCard } from "@/components/blog-card";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { ScrollReveal } from "@/components/scroll-reveal";
import { Pagination } from "@/components/pagination";
import type { BlogPost } from "@/lib/types";
import { displayCategory } from "@/lib/category-display";

interface BlogListContentProps {
  posts: BlogPost[];
  categories: string[];
  popularPosts?: BlogPost[];
}

type SortOption = "newest" | "oldest" | "title-asc" | "title-desc";

export function BlogListContent({ posts, categories, popularPosts = [] }: BlogListContentProps) {
  const searchParams = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const searchQuery = searchParams.get("q") || "";
  const sortBy = (searchParams.get("sort") || "newest") as SortOption;
  const filterCategory = searchParams.get("category") || "";

  const ITEMS_PER_PAGE = 12;

  const filteredAndSortedPosts = useMemo(() => {
    let filtered = [...posts];

    // Apply category filter
    if (filterCategory) {
      filtered = filtered.filter(
        (post) => post.category.toLowerCase() === filterCategory.toLowerCase()
      );
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(query) ||
          post.description.toLowerCase().includes(query) ||
          post.tags.some((tag) => tag.toLowerCase().includes(query)) ||
          post.category.toLowerCase().includes(query) ||
          post.author.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
        case "oldest":
          return new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime();
        case "title-asc":
          return a.title.localeCompare(b.title);
        case "title-desc":
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });

    return sorted;
  }, [posts, searchQuery, sortBy, filterCategory]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredAndSortedPosts.length / ITEMS_PER_PAGE);
  const validPage = Math.min(Math.max(1, currentPage), totalPages || 1);
  const startIndex = (validPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedPosts = filteredAndSortedPosts.slice(startIndex, endIndex);

  // Build query params for pagination links
  const queryParams: Record<string, string> = {};
  if (searchQuery) queryParams.q = searchQuery;
  if (sortBy !== "newest") queryParams.sort = sortBy;
  if (filterCategory) queryParams.category = filterCategory;

  const buildUrl = (overrides: Record<string, string> = {}) => {
    const params = new URLSearchParams();
    if (overrides.q !== undefined ? overrides.q : searchQuery) {
      params.set("q", overrides.q !== undefined ? overrides.q : searchQuery);
    }
    if (overrides.sort !== undefined ? overrides.sort : sortBy !== "newest" ? sortBy : "") {
      const s = overrides.sort !== undefined ? overrides.sort : sortBy;
      if (s && s !== "newest") params.set("sort", s);
    }
    if (overrides.category !== undefined ? overrides.category : filterCategory) {
      const c = overrides.category !== undefined ? overrides.category : filterCategory;
      if (c) params.set("category", c);
    }
    const qs = params.toString();
    return `/blog${qs ? `?${qs}` : ""}`;
  };

  return (
    <div className="py-8 md:py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Breadcrumbs
          items={[
            { name: "Home", href: "/" },
            { name: "Blog" },
          ]}
        />
        {/* Header — full width, above the two-column layout */}
        <header className="mb-12">
          <h1 className="text-3xl md:text-4xl font-serif font-semibold text-foreground">
            Who Brands Blog
          </h1>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed max-w-3xl">
            Stay informed with the latest articles about brand ownership,
            corporate structures, and consumer insights.
          </p>
        </header>

        {/* Search & Filters — full width, above the two-column layout */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <form
            action="/blog"
            method="get"
            className="relative max-w-xl"
          >
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              name="q"
              defaultValue={searchQuery}
              placeholder="Search blog posts..."
              className="w-full pl-10 pr-4 py-2.5 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent text-sm"
            />
            {filterCategory && (
              <input type="hidden" name="category" value={filterCategory} />
            )}
            {sortBy !== "newest" && (
              <input type="hidden" name="sort" value={sortBy} />
            )}
          </form>

          {/* Filters Row */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Filters:</span>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <Link
                href={buildUrl({ category: "" })}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  !filterCategory
                    ? "bg-accent text-accent-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                All
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat}
                  href={buildUrl({ category: cat })}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                    filterCategory.toLowerCase() === cat.toLowerCase()
                      ? "bg-accent text-accent-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {displayCategory(cat)}
                </Link>
              ))}
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2 sm:ml-auto">
              <label className="text-sm text-muted-foreground">Sort:</label>
              <select
                value={sortBy}
                onChange={(e) => {
                  window.location.href = buildUrl({ sort: e.target.value });
                }}
                className="px-3 py-1.5 rounded-md border border-border bg-background text-foreground text-sm"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="title-asc">Title (A-Z)</option>
                <option value="title-desc">Title (Z-A)</option>
              </select>
            </div>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground">
            {filteredAndSortedPosts.length === 0
              ? "No posts found"
              : `Showing ${startIndex + 1}-${Math.min(
                  endIndex,
                  filteredAndSortedPosts.length
                )} of ${filteredAndSortedPosts.length} posts`}
          </div>
        </div>

        {/* Two-column layout: posts list + sidebar */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Posts List — horizontal cards, one per line */}
            {paginatedPosts.length > 0 ? (
              <>
                <ScrollReveal className="flex flex-col gap-6 mb-12">
                  {paginatedPosts.map((post) => (
                    <BlogCard key={post.slug} post={post} horizontal />
                  ))}
                </ScrollReveal>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Pagination
                    currentPage={validPage}
                    totalPages={totalPages}
                    baseUrl="/blog"
                    queryParams={queryParams}
                  />
                )}
              </>
            ) : (
              <div className="text-center py-16 bg-muted rounded-lg border border-border">
                <p className="text-lg text-muted-foreground mb-2">
                  {searchQuery || filterCategory
                    ? "No blog posts found matching your criteria."
                    : "No blog posts published yet. Check back soon!"}
                </p>
                {(searchQuery || filterCategory) && (
                  <Link
                    href="/blog"
                    className="mt-4 inline-block px-4 py-2 text-sm font-medium bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors"
                  >
                    Clear filters
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Sidebar — 300px wide (excluding padding) */}
          <aside className="hidden lg:block w-[300px] flex-shrink-0" aria-label="Blog sidebar">
            {/* Popular Posts — not sticky, scrolls away */}
            {popularPosts.length > 0 && (
              <div className="mb-8 border border-border rounded-lg p-5">
                <h2 className="text-base font-semibold text-foreground uppercase tracking-wide mb-5">
                  Popular Posts
                </h2>
                <nav className="flex flex-col gap-4">
                  {popularPosts.map((post) => {
                    const thumb = post.coverImage || "/placeholder-blog.webp";
                    return (
                      <Link
                        key={post.slug}
                        href={`/blog/${post.slug}`}
                        className="group flex gap-3 items-center"
                      >
                        <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-md bg-muted">
                          <Image
                            src={thumb}
                            alt={post.title}
                            fill
                            sizes="56px"
                            className="object-cover"
                          />
                        </div>
                        <span className="text-sm font-medium text-foreground group-hover:text-accent transition-colors line-clamp-2 leading-snug">
                          {post.title}
                        </span>
                      </Link>
                    );
                  })}
                </nav>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
